import React from 'react'
import PropTypes from 'prop-types'
import { Tooltip, makeStyles } from '@material-ui/core'
import { dataMarts } from '../../../../../../api/coreData'
import { useFormattingContext } from '../../../../../organisms/FormattingProvider/FormattingContext'
import { getBatchStatus, getStatusColor } from './batchStatusHelper'

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#fff',
    whiteSpace: 'nowrap'
  },
  progress: {
    fontSize: '0.75rem',
    color: 'var(--color-text-secondary)',
    whiteSpace: 'nowrap'
  },
  dateText: {
    fontSize: '0.75rem',
    color: 'var(--color-text-secondary)',
    whiteSpace: 'nowrap',
    cursor: 'default'
  },
  neverRun: {
    fontSize: '0.75rem',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic'
  },
  errorLine: {
    fontSize: '0.75rem',
    color: 'var(--color-danger-500)',
    marginTop: '2px',
    maxWidth: '500px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}))

function extractErrorSnippet (task) {
  if (!task) return null
  const state = task.taskState
  if (!state) return null
  const msg = state.error || state.message || state.errorMessage
  if (typeof msg === 'string') return msg
  return JSON.stringify(state).slice(0, 150)
}

function getStatusDate (batch) {
  if (!batch) return null
  const { status, lastFinishedDate, lastStartedDate } = batch
  if (status === 0 || status === 1) return lastStartedDate || null
  return lastFinishedDate || lastStartedDate || null
}

function StatusDate ({ date }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  if (!date) return null

  const absolute = formatter(date, 'localDateTime')
  const since = formatter(date, 'since')

  return (
    <Tooltip title={since}>
      <span className={classes.dateText}>{absolute}</span>
    </Tooltip>
  )
}

StatusDate.propTypes = { date: PropTypes.string }

function isActiveBatch (data) {
  const status = data?.batches?.[0]?.status
  return status === 0 || status === 1
}

function useLatestBatch (timeSeriesId) {
  const { data: batchData } = dataMarts.useListDataMartBatches(timeSeriesId, {
    take: 1,
    refetchInterval: (data) => isActiveBatch(data) ? 10000 : false
  })
  const latestBatch = batchData?.batches?.[0] || null

  const isErrored = latestBatch?.status === -1 || latestBatch?.status === -3
  const { data: errorTaskData } = dataMarts.useListDataMartBatchTasks(
    latestBatch?.batchId,
    { take: 1, filters: { status: 'error' }, enabled: !!isErrored && !!latestBatch?.batchId }
  )

  return {
    latestBatch,
    isErrored,
    firstErrorTask: errorTaskData?.tasks?.[0] || null
  }
}

function DataMartStatusBadge ({ timeSeriesId, variant = 'compact' }) {
  const classes = useStyles()
  const { latestBatch, isErrored, firstErrorTask } = useLatestBatch(timeSeriesId)

  if (!latestBatch) {
    return <span className={classes.neverRun}>Never run</span>
  }

  const { label, variant: statusVariant } = getBatchStatus(latestBatch.status)
  const bgColor = getStatusColor(statusVariant)
  const errorSnippet = extractErrorSnippet(firstErrorTask)
  const statusDate = getStatusDate(latestBatch)

  const badgeEl = (
    <span className={classes.badge} style={{ backgroundColor: bgColor }}>
      {label}
    </span>
  )

  const progressText = latestBatch.totalTasks != null
    ? `${latestBatch.completedTasks || 0}/${latestBatch.totalTasks}${latestBatch.failedTasks ? ` (${latestBatch.failedTasks} err)` : ''}`
    : null

  if (variant === 'compact') {
    if (isErrored && errorSnippet) {
      return (
        <Tooltip title={`${firstErrorTask?.taskType || 'Task'}: ${errorSnippet}`}>
          <span className={classes.wrapper}>
            {badgeEl}
          </span>
        </Tooltip>
      )
    }
    return badgeEl
  }

  return (
    <div>
      <div className={classes.wrapper}>
        {badgeEl}
        {progressText && <span className={classes.progress}>{progressText} tasks</span>}
        <StatusDate date={statusDate} />
      </div>
      {isErrored && errorSnippet && (
        <div className={classes.errorLine}>
          {firstErrorTask?.taskType}: {errorSnippet}
        </div>
      )}
    </div>
  )
}

DataMartStatusBadge.propTypes = {
  timeSeriesId: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['full', 'compact'])
}

export function DataMartStatusDate ({ timeSeriesId }) {
  const classes = useStyles()
  const { latestBatch } = useLatestBatch(timeSeriesId)

  if (!latestBatch) {
    return <span className={classes.neverRun}>--</span>
  }

  const statusDate = getStatusDate(latestBatch)
  return <StatusDate date={statusDate} />
}

DataMartStatusDate.propTypes = {
  timeSeriesId: PropTypes.string.isRequired
}

export default DataMartStatusBadge
